import { css } from 'aphrodite';
import React from 'react';
import { toast } from 'sonner';
import { useQRCodeMutation } from '../../../queries';
import { Button } from '../Button';
import { LoadingSpinner } from '../LoadingSpinner';
import { IModalProps, Modal } from '../Modal';
import { styleSheet } from './styles';

export interface IQRCodeModalProps {
	modalProps: IModalProps;
	url: string;
	title?: string;
	filename?: string;
}

export const QRCodeModal: React.FC<IQRCodeModalProps> = ({
	modalProps,
	url,
	title = 'QR Code',
	filename = 'qr-code',
}) => {
	const [qrImageData, setQrImageData] = React.useState<string | null>(null);
	const qrMutation = useQRCodeMutation();

	React.useEffect(() => {
		if (modalProps.isOpen && url) {
			setQrImageData(null);

			// Parse the URL to extract baseUrl and path components
			const urlObj = new URL(url);
			const baseUrl = `${urlObj.protocol}//${urlObj.host}`;
			const path = urlObj.pathname + urlObj.search + urlObj.hash;

			qrMutation
				.mutateAsync({
					type: 'url',
					baseUrl,
					path,
					pixelsPerModule: 20,
				})
				.then(result => {
					if (result && typeof result === 'string' && result.startsWith('data:image/png;base64,')) {
						setQrImageData(result);
					} else {
						toast.error('Failed to generate QR code');
					}
				})
				.catch(() => {
					toast.error('Failed to generate QR code');
				});
		}
	}, [modalProps.isOpen, url]); // eslint-disable-line react-hooks/exhaustive-deps

	const handleCopyToClipboard = async () => {
		if (!qrImageData) return;

		try {
			const response = await fetch(qrImageData);
			const blob = await response.blob();

			if (navigator.clipboard && navigator.clipboard.write) {
				const clipboardItem = new ClipboardItem({ 'image/png': blob });
				await navigator.clipboard.write([clipboardItem]);
				toast.success('QR code copied to your clipboard!');
			} else {
				toast.error('Clipboard not supported in this browser');
			}
		} catch (error) {
			console.error('Failed to copy QR code:', error);
			toast.error('Failed to copy QR code');
		}
	};

	const handleDownload = () => {
		if (!qrImageData) return;

		const link = document.createElement('a');
		link.href = qrImageData;
		link.download = `${filename}.png`;
		link.click();
		toast.success('QR code downloaded!');
	};

	const handleClose = () => {
		modalProps.onRequestClose?.(null, true);
		setQrImageData(null);
	};

	return (
		<Modal
			{...modalProps}
			onRequestClose={handleClose}
			className={css(styleSheet.modal)}
			shouldCloseOnOverlayClick={true}
			useDefaultHeader={true}
			headerText={title}
		>
			<div className={css(styleSheet.content)}>
				{qrMutation.isPending ? (
					<div className={css(styleSheet.loadingContainer)}>
						<LoadingSpinner type='small' />
						<p>Generating QR code...</p>
					</div>
				) : qrImageData ? (
					<>
						<div className={css(styleSheet.qrContainer)}>
							<img src={qrImageData} alt='QR Code' className={css(styleSheet.qrImage)} />
						</div>
						<div className={css(styleSheet.warningText)}>
							<strong>Note:</strong> For email campaigns, we recommend copying the hyperlink instead of a QR Code for
							improved tracking.
						</div>
						<div className={css(styleSheet.buttonContainer)}>
							<Button type='button' kind='primary' onClick={handleCopyToClipboard} className={css(styleSheet.button)}>
								Copy to Clipboard
							</Button>
							<Button type='button' kind='reverse' onClick={handleDownload} className={css(styleSheet.button)}>
								Download
							</Button>
						</div>
					</>
				) : (
					<div className={css(styleSheet.errorContainer)}>
						<p>Failed to generate QR code. Please try again.</p>
						<Button type='button' kind='reverse' onClick={handleClose}>
							Close
						</Button>
					</div>
				)}
			</div>
		</Modal>
	);
};
